import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import orderService from '../services/orderService';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineFilter,
  HiOutlineRefresh,
} from 'react-icons/hi';
import './Orders.css';

const STATUS_LABELS = {
  created: 'Creada',
  accepted: 'Aceptada',
  preparing: 'Preparando',
  prepared: 'Preparada',
  in_delivery: 'En camino',
  delivered: 'Entregada',
  paid: 'Cobrada',
  closed: 'Cerrada',
  cancelled: 'Cancelada',
};

const STATUS_COLORS = {
  created: '#3b82f6',
  accepted: '#8b5cf6',
  preparing: '#f59e0b',
  prepared: '#06b6d4',
  in_delivery: '#f97316',
  delivered: '#22c55e',
  paid: '#14532d',
  closed: '#64748b',
  cancelled: '#ef4444',
};

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await orderService.getAll({ status: statusFilter || undefined });
      setOrders(data.data || []);
    } catch (err) {
      toast.error('Error cargando órdenes');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const filtered = orders.filter((o) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.client_name?.toLowerCase().includes(q) ||
      o.salesman_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="orders-page">
      <div className="page-header">
        <div>
          <h1>Órdenes</h1>
          <p>{orders.length} órdenes en total</p>
        </div>
        {hasRole('salesman', 'admin') && (
          <button className="btn-primary" onClick={() => navigate('/ordenes/nueva')}>
            <HiOutlinePlus /> Nueva Orden
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="filters-bar">
        <div className="search-box">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por orden, cliente, vendedor..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <HiOutlineFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
        <button className="btn-icon" onClick={fetchOrders} title="Actualizar">
          <HiOutlineRefresh />
        </button>
      </div>

      {/* Status chips */}
      <div className="status-chips">
        <button
          className={`chip ${statusFilter === '' ? 'active' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          Todas
        </button>
        {Object.entries(STATUS_LABELS).map(([val, label]) => {
          const count = orders.filter((o) => o.status === val).length;
          if (count === 0 && statusFilter !== val) return null;
          return (
            <button
              key={val}
              className={`chip ${statusFilter === val ? 'active' : ''}`}
              style={statusFilter === val ? { background: `${STATUS_COLORS[val]}15`, color: STATUS_COLORS[val], borderColor: `${STATUS_COLORS[val]}30` } : {}}
              onClick={() => setStatusFilter(val)}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="loading-state">
          <div className="loader"></div>
          <p>Cargando órdenes...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state-card">
          <HiOutlineFilter className="empty-icon" />
          <p>No se encontraron órdenes</p>
        </div>
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Orden</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th>Estado</th>
                <th>Tipo</th>
                <th>Total</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((order) => (
                <tr key={order.id} onClick={() => navigate(`/ordenes/${order.id}`)}>
                  <td className="mono-cell">{order.order_number}</td>
                  <td>{order.client_name || `#${order.client_id}`}</td>
                  <td>{order.salesman_name || '-'}</td>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        background: `${STATUS_COLORS[order.status]}15`,
                        color: STATUS_COLORS[order.status],
                        borderColor: `${STATUS_COLORS[order.status]}30`,
                      }}
                    >
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                  </td>
                  <td>
                    <span className={`type-badge ${order.is_credit ? 'credit' : 'cash'}`}>
                      {order.is_credit ? 'Crédito' : 'Contado'}
                    </span>
                  </td>
                  <td className="mono-cell total-cell">
                    RD$ {Number(order.total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="date-cell">
                    {order.created_at
                      ? new Date(order.created_at).toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short',
                        })
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrdersList;
